import React from 'react'
import { Prompt } from 'react-router-dom'

type Registry = (id: string, name?: string) => void

const UnsavedRulesContext = React.createContext<Registry | undefined>(undefined)

type Props = React.PropsWithChildren

const UnsavedRulesProvider: React.FC<Props> = ({ children }) => {
  const [rules, setRules] = React.useState<Record<string, string>>({})
  const names = Object.values(rules)
  const changed = names.length > 0
  const register = React.useCallback<Registry>((id, name) => {
    setRules((current) => {
      if (current[id] === name) return current
      const next = { ...current }
      if (name === undefined) delete next[id]
      else next[id] = name
      return next
    })
  }, [])

  React.useEffect(() => {
    if (!changed) return
    const handleUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', handleUnload)
    return () => window.removeEventListener('beforeunload', handleUnload)
  }, [changed])

  const message = `You have ${names.length} unsaved ${names.length === 1 ? 'rule' : 'rules'}:\n\n${names.join('\n')}\n\nReturn to editing to save each rule. If you discard these changes, they will be lost.`

  return (
    <UnsavedRulesContext.Provider value={register}>
      <Prompt when={changed} message={message} />
      {children}
    </UnsavedRulesContext.Provider>
  )
}

export const useUnsavedRule = (id: string, name: string, dirty: boolean) => {
  const register = React.useContext(UnsavedRulesContext)
  React.useLayoutEffect(() => {
    register?.(id, dirty ? name : undefined)
  }, [dirty, id, name, register])
  React.useLayoutEffect(() => () => register?.(id), [id, register])
}

export default UnsavedRulesProvider
